import { useRef } from "react";
import { useDispatch } from "react-redux";
import type { MutableRefObject } from "react";
import type { AppDispatch } from "store";
import type { ZoomRange } from "features/chart";
import { updateDrawingCoordinates } from "./thunks";
import { timestampToX } from "./drawingDragHelpers";
import { computeDragMove } from "./computeDragMove";
import { resolveHandleHit } from "./resolveHandleHit";
import type { DrawingToolInstance, ChartLayout, ChartPriceRange, DraggingOverride, DrawingCoordinates } from "./types";

const DEBOUNCE_MS = 1000;

export function useDrawingToolDrag(
  chartLayoutRef: MutableRefObject<ChartLayout>,
  priceRangeRef: MutableRefObject<ChartPriceRange | null>,
  zoomRangeRef: MutableRefObject<ZoomRange>,
  firstVisibleMsRef: MutableRefObject<number>,
  timeframeMsRef: MutableRefObject<number>,
  instancesRef: MutableRefObject<DrawingToolInstance[]>,
  onNeedsRedraw: () => void,
) {
  const dispatch = useDispatch<AppDispatch>();
  const draggingOverrideRef = useRef<DraggingOverride | null>(null);
  const draggingPointRef = useRef<"point1" | "point2" | "point3" | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const makeTsToX = () => {
    const l = chartLayoutRef.current, z = zoomRangeRef.current;
    return (ts: number) => timestampToX(ts, l, z, firstVisibleMsRef.current, timeframeMsRef.current);
  };

  const flush = (id: string, coords: DrawingCoordinates, debounce: boolean) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!debounce) { dispatch(updateDrawingCoordinates(id, coords)); return; }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      dispatch(updateDrawingCoordinates(id, coords));
    }, DEBOUNCE_MS);
  };

  const onMouseDown = (x: number, y: number): string | null => {
    const range = priceRangeRef.current;
    if (!range) return null;
    const hit = resolveHandleHit(
      x, y, instancesRef.current, draggingOverrideRef.current,
      chartLayoutRef.current, range, makeTsToX(),
    );
    if (!hit) return null;
    draggingOverrideRef.current = hit.override;
    if (hit.pointKey) draggingPointRef.current = hit.pointKey;
    return hit.cursor;
  };

  const onMouseMove = (x: number, y: number): void => {
    const dragging = draggingOverrideRef.current;
    if (!dragging) return;
    const result = computeDragMove(
      dragging, draggingPointRef.current, x, y,
      chartLayoutRef.current, priceRangeRef.current,
      zoomRangeRef.current, firstVisibleMsRef.current, timeframeMsRef.current,
    );
    if (!result) return;
    draggingOverrideRef.current = result.override;
    flush(dragging.id, result.coords, true);
    onNeedsRedraw();
  };

  const onMouseUp = (): void => {
    const dragging = draggingOverrideRef.current;
    if (!dragging) return;
    draggingOverrideRef.current = null;
    draggingPointRef.current = null;
    const coords: DrawingCoordinates | null =
      dragging.kind === "price" ? { kind: "single-price", price: dragging.price } :
      dragging.kind === "timestamp" ? { kind: "single-timestamp", timestamp: dragging.timestamp } :
      dragging.kind === "twoPoint" ? { kind: "two-point", point1: dragging.point1, point2: dragging.point2 } :
      dragging.kind === "threePoint" ? { kind: "three-point", point1: dragging.point1, point2: dragging.point2, point3: dragging.point3 } : null;
    if (coords) flush(dragging.id, coords, false);
    onNeedsRedraw();
  };

  const getCursor = (x: number, y: number): string | null => {
    const dragging = draggingOverrideRef.current;
    if (dragging?.kind === "price") return "ns-resize";
    if (dragging?.kind === "timestamp") return "ew-resize";
    if (dragging?.kind === "twoPoint" || dragging?.kind === "threePoint") return "grabbing";
    const range = priceRangeRef.current;
    if (!range) return null;
    const hit = resolveHandleHit(
      x, y, instancesRef.current, draggingOverrideRef.current,
      chartLayoutRef.current, range, makeTsToX(),
    );
    return hit?.cursor ?? null;
  };

  return { draggingOverrideRef, onMouseDown, onMouseMove, onMouseUp, getCursor };
}
